import { Star } from "lucide-react";
import { TESTIMONIALS, COMPANY } from "../lib/constants";
import useReveal from "../hooks/useReveal";

// Palette for initial avatars — evocative but accessible on white
const AVATAR_COLORS = [
  "bg-[#DB4437]", // Google red
  "bg-[#F4B400]", // Google yellow
  "bg-[#0F9D58]", // Google green
  "bg-[#4285F4]", // Google blue
  "bg-[#00B4D8]", // brand cyan
  "bg-[#0A192F]", // brand deep blue
  "bg-[#7C3AED]", // violet
  "bg-[#EA580C]", // orange
];

const getInitials = (name) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");

const GoogleLogo = ({ className = "w-4 h-4" }) => (
  <svg
    viewBox="0 0 48 48"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
    />
  </svg>
);

export default function Testimonials() {
  const head = useReveal();
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={head.ref}
          className={`fade-in-up ${head.visible ? "is-visible" : ""} max-w-3xl`}
        >
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00B4D8]">
            Reviews
          </span>
          <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] tracking-tight">
            What do our clients think about our service?
          </h2>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F8FAFC] border border-slate-200">
              <GoogleLogo className="w-4 h-4" />
              <span className="text-sm font-semibold text-[#0A192F]">
                Google Reviews
              </span>
            </div>
            <div className="inline-flex items-center gap-1.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className="w-4 h-4 text-[#F4B400] fill-[#F4B400]"
                />
              ))}
              <span className="ml-1 text-sm font-semibold text-[#0A192F]">
                5.0
              </span>
              <span className="text-sm text-slate-500">
                · Based on real customer reviews
              </span>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {TESTIMONIALS.map((t, i) => (
            <ReviewCard key={i} t={t} index={i} />
          ))}
        </div>

        <div className="mt-14 flex justify-center">
          <a
            data-testid="google-reviews-cta"
            href={COMPANY.googleReviewsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 bg-white border-2 border-[#0A192F] hover:bg-[#0A192F] hover:text-white text-[#0A192F] px-8 sm:px-10 py-4 sm:py-5 rounded-full font-semibold text-base sm:text-lg transition-all duration-300 hover:-translate-y-1 shadow-sm hover:shadow-xl"
          >
            <span className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center group-hover:bg-white">
              <GoogleLogo className="w-4 h-4" />
            </span>
            Check out more of our reviews on Google
          </a>
        </div>
      </div>
    </section>
  );
}

function ReviewCard({ t, index }) {
  const { ref, visible } = useReveal();
  const initials = getInitials(t.name);
  const color = AVATAR_COLORS[index % AVATAR_COLORS.length];

  return (
    <article
      ref={ref}
      data-testid={`review-card-${index}`}
      className={`fade-in-up ${visible ? "is-visible" : ""} relative bg-white p-6 rounded-2xl border border-slate-200 hover:border-[#00B4D8]/40 hover:shadow-xl hover:shadow-[#0A192F]/5 transition-all duration-500 hover:-translate-y-1`}
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className={`w-11 h-11 rounded-full ${color} text-white font-heading font-bold text-sm flex items-center justify-center shadow-sm`}
            aria-hidden="true"
          >
            {initials}
          </div>
          <div>
            <p className="font-heading font-semibold text-[#0A192F] text-sm leading-tight">
              {t.name}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">{t.date}</p>
          </div>
        </div>
        <GoogleLogo className="w-5 h-5 shrink-0" />
      </div>

      <div className="mt-4 flex items-center gap-0.5">
        {Array.from({ length: t.rating }).map((_, i) => (
          <Star key={i} className="w-4 h-4 text-[#F4B400] fill-[#F4B400]" />
        ))}
      </div>

      <p className="mt-3 text-[14.5px] leading-relaxed text-slate-700">
        {t.text}
      </p>
    </article>
  );
}
